import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

// Hilfsfunktion für sicheres Parsen von JSON-Strings
function safeParse(input) {
  if (!input) return {};
  if (typeof input === 'string') {
    try {
      return JSON.parse(input);
    } catch {
      return {};
    }
  }
  return input;
}

// Hilfsfunktion: Zufriedenheit in Zahl umwandeln (sonst null)
function parseSatisfaction(val) {
  if (val === null || val === undefined || val === '') return null;
  const n = Number(val);
  return isNaN(n) ? null : n;
}

export default function MentorFeedbackHistory({ userId }) {
  const [feedbacks, setFeedbacks] = useState([]);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const { t } = useTranslation();

  // Dynamische Kriterienlabels (de/en/mehr)
  const CRITERIA_LABELS = t('mentorFeedbackHistory.criteria', { returnObjects: true }) || {};

  useEffect(() => {
    if (!userId) return;
    async function fetchFeedbacks() {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/mentoring/feedback_results/mentor/${userId}`,
          { withCredentials: true }
        );
        setFeedbacks(res.data);
      } catch (err) {
        setFeedbacks([]);
      }
    }
    fetchFeedbacks();
  }, [userId]);

  // Öffnet das Detail-Popup und parst Felder, falls sie als String gespeichert sind
  const openModal = (entry) => {
    setSelectedFeedback({
      ...entry,
      goals: safeParse(entry.goals),
      competences: safeParse(entry.competences),
      criteria: safeParse(entry.criteria),
      satisfaction_score: parseSatisfaction(entry.satisfaction_score)
    });
  };

  return (
    <div className="w-full">
      <h4 className="font-semibold mb-4 text-white text-xl">{t('mentorFeedbackHistory.title')}</h4>
      <div className="grid gap-5 md:grid-cols-2">
        {feedbacks.length === 0 ? (
          <div className="bg-gray-900 text-gray-400 p-6 rounded-2xl text-center">
            {t('mentorFeedbackHistory.none')}
          </div>
        ) : (
          feedbacks.map((entry) => (
            <div
              key={entry.id}
              className="bg-gradient-to-br from-[#232629] to-[#191c1e] border border-[#24e085]/20 rounded-2xl shadow-xl p-6 flex flex-col gap-2 cursor-pointer transition hover:scale-[1.025]"
              onClick={() => openModal(entry)}
            >
              <div className="flex justify-between items-center">
                <span className="font-bold text-lg text-white">
                  {t(`milestone.${entry.milestone}`, entry.milestone)}
                </span>
                <span className="text-sm text-gray-400">
                  {entry.submitted_at ? new Date(entry.submitted_at).toLocaleDateString() : ''}
                </span>
              </div>
              <div className="flex gap-2 items-center">
                <span className="text-xs text-gray-400">{t('mentorFeedbackHistory.satisfaction')}:</span>
                <span className="text-base font-semibold text-[#24e085]">
                  {parseSatisfaction(entry.satisfaction_score) !== null
                    ? parseSatisfaction(entry.satisfaction_score) + ' / 5'
                    : '–'}
                </span>
              </div>
              <div className="truncate text-gray-300 text-sm">
                {entry.comments ? entry.comments.substring(0, 80) : t('mentorFeedbackHistory.noComment')}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Detailmodal */}
      {selectedFeedback && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
          <div className="bg-white rounded-3xl shadow-2xl px-10 py-8 max-w-sm text-center border border-[#53DD6C] relative">
            <button
              className="absolute top-4 right-4 text-[#53DD6C] text-2xl font-bold"
              onClick={() => setSelectedFeedback(null)}
              title={t('mentorFeedbackHistory.close')}
              aria-label={t('mentorFeedbackHistory.close')}
            >×</button>
            {/* Erfolg-Icon */}
            <svg className="w-16 h-16 text-[#53DD6C] mb-4 mx-auto" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" stroke="#8FFF8B" strokeWidth="2" fill="#E9FFE8"/>
              <path strokeLinecap="round" strokeLinejoin="round" d="M7 13l4 4 5-7" stroke="#53DD6C" strokeWidth="3"/>
            </svg>
            <h3 className="text-2xl font-bold mb-2 text-gray-900">
              {t('mentorFeedbackHistory.modalTitle', { milestone: t(`milestone.${selectedFeedback.milestone}`, selectedFeedback.milestone) })}
            </h3>
            <div className="mb-2 text-gray-700 text-sm">
              {selectedFeedback.submitted_at ? new Date(selectedFeedback.submitted_at).toLocaleString() : ''}
            </div>
            <div className="flex flex-col gap-2 items-center mb-6">
              <div>
                <span className="font-semibold text-gray-700">{t('mentorFeedbackHistory.satisfaction')}: </span>
                <span className="text-[#53DD6C] font-bold">
                  {selectedFeedback.satisfaction_score !== null
                    ? selectedFeedback.satisfaction_score + '/5'
                    : '–'}
                </span>
              </div>
              <div>
                <span className="font-semibold text-gray-700">{t('mentorFeedbackHistory.comment')}:</span>
                <div className="bg-gray-100 p-3 rounded mt-1 text-gray-900 w-full min-h-[32px]">
                  {selectedFeedback.comments || t('mentorFeedbackHistory.noComment')}
                </div>
              </div>
            </div>
            {/* Kriterien */}
            {selectedFeedback.criteria && Object.keys(selectedFeedback.criteria).length > 0 && (
              <div className="mb-4">
                <h4 className="font-semibold mb-2 text-[#53DD6C]">{t('mentorFeedbackHistory.criteriaTitle')}</h4>
                <ul className="list-disc ml-6 text-left text-gray-900">
                  {Object.entries(selectedFeedback.criteria).map(([key, val]) => (
                    <li key={key}>
                      <span className="font-medium">{CRITERIA_LABELS[key] || key}</span>: {val}/5
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <button
              className="mt-6 px-6 py-3 bg-gradient-to-r from-[#8FFF8B] to-[#53DD6C] text-black rounded-xl font-bold shadow hover:from-[#53DD6C] hover:to-[#8FFF8B] transition text-lg"
              onClick={() => setSelectedFeedback(null)}
              autoFocus
            >
              {t('mentorFeedbackHistory.close')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

